import { useCallback, useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import { Typography } from '@getsynapse/design-system';
import useModal from 'Hooks/useModal';
import deleteIcon from 'assets/icons/trash-bin-small.svg';

interface DeleteInsightModalProps {
  gridId: string;
  shouldDisplayModal: boolean;
  insightName: string;
  onCloseModal: () => void;
  deleteInsight: (insightId: string) => void;
}
const DeleteInsightModal = ({
  gridId: insightId,
  insightName,
  shouldDisplayModal,
  onCloseModal,
  deleteInsight,
}: DeleteInsightModalProps) => {
  const { Modal, modalProps, openModal, closeModal } = useModal();
  const action = intl.get('DELETE');

  const handleCloseModal = useCallback(() => {
    onCloseModal();
    closeModal();
  }, [onCloseModal, closeModal]);

  const confirmDeleteInsight = useCallback(async () => {
    deleteInsight(insightId);
    handleCloseModal();
  }, [insightId, deleteInsight, handleCloseModal]);

  useEffect(() => {
    if (shouldDisplayModal && !modalProps.isOpen) {
      openModal(modalProps);
    }
  }, [modalProps, openModal, shouldDisplayModal]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('DELETE_INSIGHT_MODAL.DELETE_BUTTON'),
        variant: 'primary',
        color: 'danger',
        onClick: confirmDeleteInsight,
        'data-testid': 'confirm-button',
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModal,
        'data-testid': 'cancel-button',
      },
    ],
    [confirmDeleteInsight, handleCloseModal]
  );

  return (
    <Modal
      {...modalProps}
      title={intl.get('DELETE_INSIGHT_MODAL.TITLE')}
      titleIcon={{
        name: 'trash-outline',
        className: 'text-error-dark',
        src: deleteIcon,
      }}
      size='small'
      aria-label={intl.get('DELETE_INSIGHT_MODAL.TITLE', { action })}
      childrenClassName='flex flex-col space-y-6'
      data-testid='delete-insight-modal'
      closeModal={handleCloseModal}
      actionButtons={actionButtons}
    >
      <Typography variant='body' data-testid='delete-caption'>
        {intl.get('DELETE_INSIGHT_MODAL.CAPTION')}
      </Typography>
    </Modal>
  );
};

export default DeleteInsightModal;
